import React, { useState } from 'react';

const N8nOrbitalChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, type: 'bot', text: 'Welcome to Orbital IQ! How can I assist with your satellite data queries today?' }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('AI Agent processing');

  // Inject animation styles when component mounts
  React.useEffect(() => {
    if (!document.getElementById('chat-bounce-styles')) {
      const style = document.createElement('style');
      style.id = 'chat-bounce-styles';
      style.textContent = `
        @keyframes chatDotBounce {
          0%, 80%, 100% {
            transform: translateY(0px) scale(1);
            opacity: 0.5;
          }
          40% {
            transform: translateY(-10px) scale(1.2);
            opacity: 1;
          }
        }
        
        @keyframes chatPulse {
          0%, 100% {
            opacity: 0.5;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.05);
          }
        }
        
        @keyframes chatTyping {
          0%, 60%, 100% {
            opacity: 0;
          }
          30% {
            opacity: 1;
          }
        }
        
        @keyframes chatThinking {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes orbitRotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .chat-loading-dot {
          animation: chatDotBounce 1.6s infinite ease-in-out;
        }
        .chat-loading-dot:nth-child(1) { animation-delay: 0s; }
        .chat-loading-dot:nth-child(2) { animation-delay: 0.2s; }
        .chat-loading-dot:nth-child(3) { animation-delay: 0.4s; }
        
        .chat-loading-container {
          animation: chatPulse 2s infinite ease-in-out;
        }
        
        .chat-thinking-indicator {
          animation: chatThinking 2s linear infinite;
        }
        
        .orbit-ring {
          animation: orbitRotate 3s linear infinite;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Cycle through different loading messages
  React.useEffect(() => {
    if (isLoading) {
      const messages = [
        'AI Agent processing',
        'Analyzing satellite data',
        'Querying orbital database',
        'Fetching data',
        'Processing your request'
      ];
      let index = 0;
      
      const interval = setInterval(() => {
        index = (index + 1) % messages.length;
        setLoadingMessage(messages[index]);
      }, 2000);
      
      return () => clearInterval(interval);
    }
  }, [isLoading]);

  // Refresh function to clear chat and start new session
  const refreshChat = () => {
    console.log('Refreshing chat session...');
    
    // Clear messages and reset to initial state
    setMessages([
      { id: 1, type: 'bot', text: 'Welcome to Orbital IQ! How can I assist with your satellite data queries today?' }
    ]);
    
    // Clear input
    setInputValue('');
    
    // Clear session storage to start fresh
    localStorage.removeItem('n8n-chat-session');
    
    // Stop any loading state
    setIsLoading(false);
    
    console.log('Chat session refreshed successfully');
  };
  
  const sendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = { 
      id: Date.now(), 
      type: 'user', 
      text: inputValue 
    };
    
    const currentInput = inputValue;
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      console.log('Sending message to n8n:', currentInput);
      console.log('🚀 Using 30-second timeout - Updated version loaded!');
      
      // Add timeout (30 seconds for complex AI agent workflows)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('❌ Request aborted after 30 seconds');
        controller.abort();
      }, 600000);
      
      // Format payload according to n8n documentation
      const payload = {
        chatInput: currentInput,  // This is the key n8n expects for AI Agent
        sessionId: localStorage.getItem('n8n-chat-session') || 'session-' + Date.now(),
        action: 'sendMessage'  // Required action parameter
      };
      
      // Store session ID for future requests
      localStorage.setItem('n8n-chat-session', payload.sessionId);
      
      console.log('Sending n8n payload:', payload);
      
      const response = await fetch('https://n8n-6ihl.onrender.com/webhook/408b1ec7-6bd5-4057-a71c-e01ba369924b/chat?action=sendMessage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      console.log('N8n response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('N8n error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // Handle response
      const contentType = response.headers.get('content-type');
      let botResponseText = '';
      
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        console.log('Received n8n data:', data);
        
        // n8n might return response in different formats
        if (data.output) {
          botResponseText = data.output;
        } else if (data.response) {
          botResponseText = data.response;
        } else if (data.message) {
          botResponseText = data.message;
        } else if (data.text) {
          botResponseText = data.text;
        } else if (typeof data === 'string') {
          botResponseText = data;
        } else {
          botResponseText = 'I received your message!';
          console.log('Unexpected n8n response format:', data);
        }
      } else {
        botResponseText = await response.text();
        console.log('Non-JSON n8n response:', botResponseText);
      }
      
      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: botResponseText || 'I received your message!'
      };
      
      setMessages(prev => [...prev, botMessage]);
      
    } catch (error) {
      console.error('N8n chat error:', error);
      
      let errorMessage = '';
      
      if (error.name === 'AbortError') {
        errorMessage = 'Request timed out after 30 seconds. Your AI agent workflow is taking longer than expected. This could be due to complex database queries or AI processing. You can explore the dashboard data above for immediate satellite information.';
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Unable to connect to the AI service. Please check your internet connection or try again later. The dashboard data above is still available for exploration.';
      } else if (error.message.includes('HTTP 500')) {
        errorMessage = 'The AI workflow encountered an error. This might be due to the n8n workflow configuration. You can explore the interactive satellite dashboard above for detailed information.';
      } else if (error.message.includes('HTTP 404')) {
        errorMessage = 'AI endpoint not found. The n8n workflow might not be active. You can still use the satellite dashboard above to explore orbit data, contractors, and launch information.';
      } else {
        // Provide intelligent fallback based on query content
        const lowerInput = currentInput.toLowerCase();
        
        if (lowerInput.includes('leo') || lowerInput.includes('low earth orbit')) {
          errorMessage = 'LEO (Low Earth Orbit) satellites typically operate between 160-2,000 km altitude. Most modern satellite constellations like Starlink operate in LEO for lower latency. The dashboard shows detailed LEO satellite data in the Orbit Analysis section.';
        } else if (lowerInput.includes('contractor') || lowerInput.includes('company')) {
          errorMessage = 'Based on the current satellite database, SpaceX leads in satellite deployments, followed by Planet Labs and other major contractors. You can see the complete contractor analysis in the dashboard\'s contractor section.';
        } else if (lowerInput.includes('launch') || lowerInput.includes('year')) {
          errorMessage = 'Satellite launches have increased dramatically in recent years, with thousands launched annually. The dashboard\'s timeline chart shows deployment trends by country and year.';
        } else if (lowerInput.includes('geo') || lowerInput.includes('geostationary')) {
          errorMessage = 'GEO (Geostationary) satellites orbit at ~35,786 km altitude and appear stationary relative to Earth. They\'re used for communications and weather monitoring. The orbit analysis section shows GEO vs LEO comparisons.';
        } else {
          errorMessage = 'AI service temporarily unavailable. You can explore the interactive satellite dashboard above for detailed information about orbits, contractors, launch data, and satellite purposes.';
        }
      }
      
      const errorResponse = {
        id: Date.now() + 1,
        type: 'bot',
        text: errorMessage + (error.message.includes('HTTP') ? '' : '\n\n💡 Note: AI service temporarily unavailable - response generated from dashboard data.')
      };
      setMessages(prev => [...prev, errorResponse]);
    } finally {
      setIsLoading(false);
      setLoadingMessage('AI Agent processing');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedQuestions = [
    "What is the average perigee of all satellites in LEO?",
    "How many commercial satellites were launched in 2022?",
    "Which contractor has the most satellites?",
    "How many Starlink satellites are in orbit compared to OneWeb?",
  ];

  // Enhanced test connection function
  const testConnection = async () => {
    console.log('=== N8N CONNECTION TEST (Updated for n8n Chat Protocol) ===');
    
    // Test 1: Load previous session (as per n8n documentation)
    try {
      console.log('Test 1: Load previous session...');
      const sessionResponse = await fetch('https://n8n-6ihl.onrender.com/webhook/408b1ec7-6bd5-4057-a71c-e01ba369924b/chat?action=loadPreviousSession', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: 'test-session',
          action: 'loadPreviousSession'
        })
      });
      console.log('Session Response status:', sessionResponse.status);
      const sessionText = await sessionResponse.text();
      console.log('Session Response body:', sessionText);
    } catch (error) {
      console.error('Session test failed:', error);
    }

    // Test 2: Send test message with proper n8n format
    try {
      console.log('Test 2: Send message with n8n format...');
      const messageResponse = await fetch('https://n8n-6ihl.onrender.com/webhook/408b1ec7-6bd5-4057-a71c-e01ba369924b/chat?action=sendMessage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chatInput: 'test message',
          sessionId: 'test-session',
          action: 'sendMessage'
        })
      });
      console.log('Message Response status:', messageResponse.status);
      const messageText = await messageResponse.text();
      console.log('Message Response body:', messageText);
    } catch (error) {
      console.error('Message test failed:', error);
    }

    // Test 3: Check if Chat Trigger is configured
    try {
      console.log('Test 3: Basic connectivity test...');
      const basicResponse = await fetch('https://n8n-6ihl.onrender.com/webhook/408b1ec7-6bd5-4057-a71c-e01ba369924b/chat', {
        method: 'GET',
      });
      console.log('Basic Response status:', basicResponse.status);
      const basicText = await basicResponse.text();
      console.log('Basic Response body:', basicText);
    } catch (error) {
      console.error('Basic test failed:', error);
    }

    console.log('=== END N8N CONNECTION TEST ===');
    console.log('💡 Make sure your n8n workflow:');
    console.log('1. Uses a Chat Trigger node (not Webhook)');
    console.log('2. Has this domain in Allowed Origins (CORS)');
    console.log('3. Is Active and deployed');
  };

  // Professional SVG icons
  const ChatIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C6.48 2 2 6.48 2 12C2 13.86 2.62 15.58 3.68 16.95L2.5 21.5L7.05 20.32C8.42 21.38 10.14 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2Z" stroke="#f1f5f9" strokeOpacity="0.9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="8" cy="12" r="1" fill="#f1f5f9" fillOpacity="0.9"/>
      <circle cx="12" cy="12" r="1" fill="#f1f5f9" fillOpacity="0.9"/>
      <circle cx="16" cy="12" r="1" fill="#f1f5f9" fillOpacity="0.9"/>
    </svg>
  );

  const OrbitIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="orbit-ring">
      <circle cx="12" cy="12" r="2" fill="#1E90FF"/>
      <path d="M12 2C17.52 2 22 6.48 22 12C22 17.52 17.52 22 12 22" stroke="#1E90FF" strokeWidth="1.5" strokeDasharray="2 2"/>
      <path d="M2 12C2 6.48 6.48 2 12 2" stroke="#00CED1" strokeWidth="1.5" strokeDasharray="2 2"/>
      <circle cx="19" cy="12" r="1.5" fill="#00CED1"/>
      <circle cx="5" cy="12" r="1.5" fill="#1E90FF"/>
    </svg>
  );

  return (
    <>
      {/* Chat Button */}
      <div 
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          top: '30px',
          right: '30px',
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #1a365d 0%, #2c5282 100%)',
          border: '1px solid rgba(59, 130, 246, 0.5)',
          boxShadow: '0 4px 24px rgba(59, 130, 246, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          transition: 'all 0.2s ease',
          color: '#f1f5f9'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 30px rgba(59, 130, 246, 0.25), 0 2px 4px rgba(0, 0, 0, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 24px rgba(59, 130, 246, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)';
        }}
      >
        <ChatIcon />
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          top: '110px',
          right: '30px',
          width: '380px',
          height: '600px',
          backgroundColor: 'rgb(40, 46, 63)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          zIndex: 99998,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #1a365d 0%, #2c5282 100%)',
            color: 'rgba(190, 194, 198, 0.96)',
            padding: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
          }}>
            <div>
              <div style={{ fontSize: '18px', fontWeight: '600', letterSpacing: '-0.025em', color: 'rgba(190, 194, 198, 0.96)', opacity: 0.95 }}>Orbital IQ Assistant</div>
              <div style={{ fontSize: '13px', color: '#f1f5f9', opacity: 0.7, marginTop: '2px' }}>Satellite Intelligence Platform</div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {/* Refresh Button */}
              <button
                onClick={refreshChat}
                style={{
                  background: 'rgba(241, 245, 249, 0.1)',
                  border: '1px solid rgba(241, 245, 249, 0.2)',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  color: '#f1f5f9',
                  fontSize: '18px',
                  opacity: 0.9
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(241, 245, 249, 0.2)';
                  e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(241, 245, 249, 0.1)';
                  e.currentTarget.style.opacity = '0.9';
                }}
                title="Start New Session"
              >
                ↻
              </button>
              
              {/* Close Button */}
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'rgba(241, 245, 249, 0.1)',
                  border: '1px solid rgba(241, 245, 249, 0.2)',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  color: '#f1f5f9',
                  fontSize: '20px',
                  lineHeight: '1',
                  opacity: 0.9
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
                  e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(241, 245, 249, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(241, 245, 249, 0.2)';
                  e.currentTarget.style.opacity = '0.9';
                }}
              >
                ×
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div style={{
            flex: 1,
            backgroundColor: '#0f172a',
            padding: '20px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {messages.map((message) => (
              <div key={message.id} style={{
                display: 'flex',
                justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start'
              }}>
                <div style={{
                  maxWidth: '75%',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  backgroundColor: message.type === 'user' 
                    ? '#2563eb'
                    : '#1e293b',
                  
                  opacity: 0.95,
                  fontSize: '14px',
                  lineHeight: '1.5',
                  wordWrap: 'break-word',
                  boxShadow: message.type === 'user'
                    ? '0 2px 4px rgba(37, 99, 235, 0.2)'
                    : '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  {message.text}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start'
              }}>
                <div 
                  className="chat-loading-container"
                  style={{
                    padding: '16px 20px',
                    borderRadius: '12px',
                    backgroundColor: '#1e293b',
                    border: '1px solid rgba(59, 130, 246, 0.1)',
                    opacity: 0.95,
                    fontSize: '14px',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* Rotating orbit indicator */}
                    <OrbitIcon />
                    
                    <div>
                      <div style={{ fontWeight: '500', color: '#f1f5f9', opacity: 0.9 }}>{loadingMessage}</div>
                      <div style={{ 
                        display: 'flex', 
                        gap: '4px',
                        alignItems: 'center',
                        marginTop: '8px'
                      }}>
                        <div 
                          className="chat-loading-dot"
                          style={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: '#3b82f6'
                          }}
                        />
                        <div 
                          className="chat-loading-dot"
                          style={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: '#06b6d4'
                          }}
                        />
                        <div 
                          className="chat-loading-dot"
                          style={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: '#3b82f6'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Progress indicator */}
                  <div style={{
                    marginTop: '12px',
                    height: '2px',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    borderRadius: '1px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: '30%',
                      backgroundColor: '#3b82f6',
                      animation: 'progress 3s ease-in-out infinite',
                      background: 'linear-gradient(90deg, transparent, #3b82f6, transparent)',
                    }}/>
                  </div>
                </div>
              </div>
            )}

            {/* Suggested Questions */}
            {messages.length === 1 && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '12px', fontWeight: '500' }}>
                  Suggested queries
                </div>
                {suggestedQuestions.map((question, index) => (
                  <div
                    key={index}
                    onClick={() => setInputValue(question)}
                    style={{
                      padding: '10px 14px',
                      margin: '6px 0',
                      backgroundColor: '#1e293b',
                      border: '1px solid rgba(59, 130, 246, 0.1)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      color: '#94a3b8',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#334155';
                      e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                      e.currentTarget.style.color = '#f1f5f9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#1e293b';
                      e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.1)';
                      e.currentTarget.style.color = '#94a3b8';
                    }}
                  >
                    {question}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Input Area */}
          <div style={{
            backgroundColor: '#0f172a',
            padding: '20px',
            borderTop: '1px solid rgba(59, 130, 246, 0.1)'
          }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about satellite data..."
                disabled={isLoading}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: '#1e293b',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: '8px',
                  color: '#f1f5f9',
                  fontSize: '14px',
                  outline: 'none',
                  opacity: isLoading ? 0.5 : 1,
                  cursor: isLoading ? 'not-allowed' : 'text',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => {
                  if (!isLoading) {
                    e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                    e.target.style.backgroundColor = '#334155';
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(59, 130, 246, 0.2)';
                  e.target.style.backgroundColor = '#1e293b';
                }}
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !inputValue.trim()}
                style={{
                  padding: '12px 24px',
                  backgroundColor: isLoading || !inputValue.trim() 
                    ? '#334155' 
                    : '#2563eb',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#f1f5f9',
                  opacity: isLoading || !inputValue.trim() ? 0.6 : 0.95,
                  cursor: isLoading || !inputValue.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  minWidth: '80px'
                }}
                onMouseEnter={(e) => {
                  if (!isLoading && inputValue.trim()) {
                    e.target.style.backgroundColor = '#1d4ed8';
                    e.target.style.opacity = '1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading && inputValue.trim()) {
                    e.target.style.backgroundColor = '#2563eb';
                    e.target.style.opacity = '0.95';
                  }
                }}
              >
                {isLoading ? '...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add progress animation keyframe and remove test button from UI */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes progress {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}} />
    </>
  );
};

export default N8nOrbitalChat;